import { useMemo, useState } from 'react';
import { irfConfig } from '../IrfConfig';
import { InternalIrfPublicationProps } from './fetchData';
import { extractTypeCodes, filterByTypeCode } from '../index';
import { splitStudentTheses } from './splitStudentTheses';

export interface TabState {
  publicationTab: InternalIrfPublicationProps[];
  projectTab: InternalIrfPublicationProps[];
  presentationTab: InternalIrfPublicationProps[];
  supervisedThesesTab: InternalIrfPublicationProps[];
  othersTab: InternalIrfPublicationProps[];
}

export const useTabs = (
  jsonData: InternalIrfPublicationProps[],
  selectedTypes: string[] | null,
  person: string | null,
  personName: string | null = null,
) => {
  const selectedPublicationTypes = extractTypeCodes(selectedTypes || []);

  const tabs = useMemo(() => {
    // Get all publications for the publication tab (excluding type 11)
    const publicationsWithoutTheses = filterByTypeCode(
      jsonData,
      selectedPublicationTypes,
      irfConfig.publicationTab.filter((code) => code !== '11'),
    );

    // Get all student theses (type 11)
    const studentTheses = filterByTypeCode(jsonData, selectedPublicationTypes, [
      '11',
    ]);

    // Split theses into the person's own (-> publications tab) and the
    // ones the person supervised (-> supervised theses tab)
    const { ownTheses, supervisedTheses } = splitStudentTheses(
      studentTheses,
      person,
      personName,
    );

    return {
      publicationTab: [
        ...publicationsWithoutTheses,
        ...ownTheses, // Theses authored by the person belong to publicationTab
      ],
      projectTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.projectTab,
      ),
      presentationTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.presentationTab,
      ),
      supervisedThesesTab: supervisedTheses, // Contains theses supervised by person (not authored)
      othersTab: filterByTypeCode(
        jsonData,
        selectedPublicationTypes,
        irfConfig.othersTab,
      ),
    };
  }, [person, personName, jsonData, selectedPublicationTypes]);

  const [activeTab, setActiveTab] = useState<keyof TabState>(() => {
    // Use the first tab with data. On the very first render jsonData is still
    // empty (it is fetched asynchronously), so this resolves to publicationTab;
    // the effect in IrfPublication moves it on once the data has arrived.
    const firstTabWithData = Object.entries(tabs).find(
      ([_, entries]) => entries.length > 0,
    );
    return (firstTabWithData?.[0] as keyof TabState) || 'publicationTab';
  });

  return { tabs, activeTab, setActiveTab };
};

export const useActiveTabs = (
  tabs: TabState,
  showOnlyPeerReviewed: boolean,
  excludedPublications: string[] = [],
) => {
  return useMemo(
    () =>
      Object.entries(tabs).filter(([tabKey, entries]) => {
        const filteredEntries = entries.filter(
          (entry: InternalIrfPublicationProps) =>
            !excludedPublications.includes(entry.url_handle?.toString() || ''),
        );
        return (
          filteredEntries.length > 0 &&
          (tabKey === 'projectTab' ||
            !showOnlyPeerReviewed ||
            filteredEntries.some(
              (entry: InternalIrfPublicationProps) => entry.peer_reviewed,
            ))
        );
      }),
    [tabs, showOnlyPeerReviewed, excludedPublications],
  );
};
